import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { ph } from '../lib/posthog';

interface Referral {
  referral_code: string;
  status: string;
  created_at: string;
}

interface ReferralStats {
  referralCode: string;
  invited: number;
  pending: number;
  converted: number;
  monthsEarned: number;
}

export function ReferralPage() {
  const { profile }          = useAuth();
  const [stats,  setStats]   = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied]   = useState(false);

  useEffect(() => {
    if (profile?.id) loadStats();
  }, [profile?.id]);

  async function loadStats() {
    if (!profile?.id) return;
    const { data } = await supabase
      .from('referrals')
      .select('referral_code, status, created_at')
      .eq('referrer_id', profile.id)
      .order('created_at');

    const rows = (data ?? []) as Referral[];
    const code      = rows[0]?.referral_code ?? generateCode(profile.id);
    const invited   = rows.length;
    const pending   = rows.filter(r => r.status === 'pending').length;
    const converted = rows.filter(r => r.status === 'converted').length;
    const months    = converted;

    setStats({ referralCode: code, invited, pending, converted, monthsEarned: months });
    setLoading(false);
  }

  function generateCode(userId: string): string {
    return userId.slice(0, 8).toUpperCase();
  }

  const referralLink = stats ? `https://professya.com/ref/${stats.referralCode}` : '';

  async function copyLink() {
    await navigator.clipboard.writeText(referralLink);
    setCopied(true);
    ph.referralShared('copy');
    setTimeout(() => setCopied(false), 2500);
  }

  function shareWhatsApp() {
    if (!stats) return;
    const firstName = profile?.full_name?.split(' ')[0] ?? 'eu';
    const text = encodeURIComponent(
      `${firstName} te convidou para o Professya, astrologia que transforma! Ganhe 30 dias grátis ao criar sua conta:\n${referralLink}`
    );
    ph.referralShared('whatsapp');
    window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
  }

  function shareTwitter() {
    if (!stats) return;
    const text = encodeURIComponent(
      `Descobri o Professya, astrologia personalizada que vai além do horóscopo! Cria sua conta grátis e ganha 30 dias Premium: ${referralLink}`
    );
    ph.referralShared('twitter');
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  }

  function shareEmail() {
    if (!stats) return;
    const firstName = profile?.full_name?.split(' ')[0] ?? 'eu';
    const subject = encodeURIComponent(`${firstName} te convidou para o Professya`);
    const body = encodeURIComponent(
      `Olá!\n\n${firstName} te convidou para o Professya, astrologia personalizada que vai além do horóscopo.\n\nCrie sua conta grátis e ganhe 30 dias Premium:\n${referralLink}\n\nAté breve!`
    );
    ph.referralShared('email');
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  const qrUrl = referralLink
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(referralLink)}&bgcolor=1A1D35&color=F5C842&qzone=2`
    : '';

  if (loading) {
    return (
      <div className="page-container">
        <div className="card shimmer" style={{ height: '200px' }} />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          Indicações
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Compartilhe e ganhe meses grátis de Premium
        </p>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Convidados', value: stats?.invited ?? 0, emoji: '👥' },
          { label: 'Pendentes',  value: stats?.pending ?? 0,  emoji: '⏳' },
          { label: 'Convertidos', value: stats?.converted ?? 0, emoji: '✅' },
          { label: 'Meses ganhos', value: stats?.monthsEarned ?? 0, emoji: '🎁' },
        ].map(s => (
          <div key={s.label} className="card" style={{ flex: 1, textAlign: 'center', padding: '0.75rem 0.25rem' }}>
            <span style={{ fontSize: '1.125rem', display: 'block', marginBottom: '0.2rem' }}>{s.emoji}</span>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.375rem', color: 'var(--petrol)', fontWeight: 700, margin: '0 0 0.15rem' }}>{s.value}</p>
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0, lineHeight: 1.2 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Referral link + QR */}
      <div className="card" style={{ marginBottom: '1.5rem', border: '1px solid var(--border-gold)', backgroundColor: 'rgba(31,78,95,0.04)' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
          ✦ Seu link de indicação
        </p>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* QR Code */}
          {qrUrl && (
            <div style={{
              padding: '8px',
              backgroundColor: '#1A1D35',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-gold)',
              flexShrink: 0,
            }}>
              <img src={qrUrl} alt="QR Code do link de indicação" width={88} height={88} style={{ display: 'block', borderRadius: '4px' }} />
            </div>
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <code style={{
                flex: 1, padding: '0.5rem 0.75rem',
                backgroundColor: 'var(--bg-base)', borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--text-caption)', color: 'var(--text-secondary)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                border: '1px solid var(--border-subtle)',
              }}>
                {referralLink}
              </code>
              <button className="btn btn-secondary btn-sm" onClick={copyLink} style={{ flexShrink: 0 }}>
                {copied ? '✓ Copiado' : 'Copiar'}
              </button>
            </div>
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
              Mostre o QR Code pessoalmente ou copie o link para compartilhar online.
            </p>
          </div>
        </div>
      </div>

      {/* Share buttons */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.875rem' }}>
          ✦ Compartilhar via
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <button
            className="btn btn-primary"
            onClick={shareWhatsApp}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            💬 WhatsApp
          </button>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn btn-secondary"
              onClick={shareTwitter}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
            >
              𝕏 Twitter / X
            </button>
            <button
              className="btn btn-secondary"
              onClick={shareEmail}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
            >
              ✉️ Email
            </button>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="card">
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1rem' }}>
          ✦ Como funciona
        </p>
        <ol style={{ margin: 0, padding: '0 0 0 1.25rem' }}>
          {[
            'Compartilhe seu link único com amigos, família ou seguidores',
            'Seu amigo cria uma conta gratuita usando seu link',
            'Quando ele assinar qualquer plano, vocês dois ganham 30 dias grátis de Premium',
            'Sem limite, quanto mais indicações convertidas, mais meses você acumula!',
          ].map((step, i) => (
            <li key={i} style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.6rem', lineHeight: 1.5 }}>
              {step}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
